// src/components/BookingCard/index.js
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { pinSvgIconPink } from "assets/icons";
import { FormButton, CloseButton } from "reusableComponents";
import { transformBookingData } from "services/booking.services";
import { useNavigate } from "react-router";

import { useLanguage, useModal } from "context";
import { useSelector } from "react-redux";
import { selectIsAuthenticated, selectUserType } from "store/auth/selectors";

import styles from "./styles.module.scss";
import { getDispositionTypeByKey } from "helpers/getSpaceDispositionTypes";
import { formatNumberWithSpaces } from "helpers/formatNumberWithSpaces";

const LAYOUTS = {
  BOOKING_HISTORY: "bookingHistory",
  CURRENT_BOOKINGS: "currentBookings",
  REVIEWS: "reviews",
};

// Helper to check if a service is paid onsite - MATCHES ServicesSection logic
const isServicePaidOnsite = (service) => {
  // Check from the service data structure (same as ServicesSection)
  if (service?.location_type === "onsite") return true;
  if (service?.type === "onsite") return true;
  if (service?.service?.location_type === "onsite") return true;
  if (service?.service?.type === "onsite") return true;
  if (service?.service?.place_service?.type === "onsite") return true;
  // Check if service has paid onsite indicator in name
  if (service?.name?.toLowerCase().includes("onsite")) return true;
  return false;
};

// Helper to format service name - NAME ONLY (no type, no quantity)
const formatServiceName = (service) => {
  return service?.name || 'Service';
};

// Helper to format quantity with "x" prefix and unit
const formatQuantityWithUnit = (service) => {
  const quantity = service?.quantity || 1;
  const type = service?.type;
  
  // If type is null or empty, just show "x {number}"
  if (!type || type === 'null' || type === '' || type === 'undefined') {
    return `x ${quantity}`;
  }
  
  // Format type for display
  let typeDisplay = type;
  if (type === 'hour') typeDisplay = 'hour';
  else if (type === 'halfDay') typeDisplay = 'half day';
  else if (type === 'fullDay') typeDisplay = 'full day';
  else if (type === 'day') typeDisplay = 'day';
  else if (type === 'included') return `x ${quantity}`;
  
  // Handle pluralization with "x" prefix
  if (quantity > 1) {
    if (typeDisplay === 'hour') return `x ${quantity} hours`;
    if (typeDisplay === 'day') return `x ${quantity} days`;
    if (typeDisplay === 'half day') return `x ${quantity} half days`;
    if (typeDisplay === 'full day') return `x ${quantity} full days`;
    return `x ${quantity} ${typeDisplay}s`;
  }
  
  return `x ${quantity} ${typeDisplay}`;
};

// Helper to get total price - for services_per_days, price is already the total
const getTotalPrice = (service) => {
  return service?.price || 0;
};

// Helper to check if service is free/included
const isServiceFree = (service) => {
  return service?.price === 0 || service?.location_type === "included" || service?.type === "included";
};

// ServiceItem component with 3-column layout and Paid Onsite badge
const ServiceItem = ({ service, showPrice = true, t }) => {
  const isFree = isServiceFree(service);
  const isPaidOnsite = isServicePaidOnsite(service);
  const totalPrice = getTotalPrice(service);
  const serviceName = formatServiceName(service);
  const quantityDisplay = formatQuantityWithUnit(service);
  
  return (
    <div className={styles.serviceItem}>
      {/* Column 1: Service Name with badges */}
      <div className={styles.serviceNameWrapper}>
        <span className={styles.serviceName}>
          {serviceName}
        </span>
        {isPaidOnsite && (
          <span 
            className={styles.paidOnsiteBadge}
            title={t("booking.fullBookingPage.services.paidOnsiteTooltip", "This service is paid directly at the venue")}
          >
            💳 Pay on-site
          </span>
        )}
        {isFree && !isPaidOnsite && (
          <span className={styles.freeBadge}>
            {t("booking.fullBookingPage.services.free", "Free")}
          </span>
        )}
      </div>
      
      {/* Column 2: Quantity with "x" prefix (bold, left-aligned) */}
      <div className={styles.serviceQuantity}>
        {quantityDisplay}
      </div>
      
      {/* Column 3: Price (bold, right-aligned) */}
      {showPrice && !isFree && (
        <div className={styles.servicePrice}>{totalPrice} €</div>
      )}
      {showPrice && isFree && (
        <div className={styles.servicePrice}>-</div>
      )}
    </div>
  );
};

// Group services by day from services_per_days
const groupServicesByDay = (servicesPerDays) => {
  if (!servicesPerDays || servicesPerDays.length === 0) return [];
  
  const grouped = {};
  servicesPerDays.forEach(service => {
    const date = service.date || 'unknown';
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push({
      name: service.name || 'Service',
      quantity: service.quantity || 1,
      price: service.price || 0,
      type: service.type || null,
      location_type: service.location_type || 'day',
      description: service.description || '',
    });
  });
  
  // Sort dates
  return Object.keys(grouped).sort().map(date => ({
    date,
    services: grouped[date]
  }));
};

const BookingCard = ({
  booking,
  layout = LAYOUTS.CURRENT_BOOKINGS,
  onReject = () => {},
  onApprove = () => {},
  onUserCancel = () => {},
  actionLoading = false,
  actionError = null,
  updateBookings = () => {},
}) => {
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { language } = useLanguage;
  const { t } = useTranslation("global");
  const userType = useSelector(selectUserType);
  const [selectedStatus, setSelectedStatus] = useState(booking.status);
  const [showDetails, setShowDetails] = useState(false);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const accessToken = useSelector((state) => state.auth.token);

  const {
    place,
    total_with_vat,
    total,
    vat,
    capacity,
    coworking_stations,
    disposition_type,
    services,
    id,
    promo_title,
    user,
    services_per_days,
  } = booking;

  const getBookingStatus = (booking, userType) => {
    if (!booking.loading) {
      if (booking.status === "approved") {
        return booking.paid 
          ? t("dashboard.bookings.paid")
          : ((booking.total === 0 || booking.trainee_booked) 
            ? t("dashboard.bookings.confirmed") 
            : t("dashboard.bookings.paymentWaiting"));
      }
      if (booking.status === "waiting") {
        return t("dashboard.bookings.pending");
      }
      if (booking.status === "rejected") {
        return t("dashboard.bookings.cancelled");
      }
    }
    return null;
  };

  const handleResponseChange = (newStatus) => {
    if (newStatus === "rejected") {
      onReject();
    } else if (newStatus === "approved") {
      onApprove();
    }
    setSelectedStatus(newStatus);
  };
  
  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const dispositionType = getDispositionTypeByKey(disposition_type, t);

  const { booking_hours } = transformBookingData(booking);

  const formattedTotalWithVAT = formatNumberWithSpaces(total_with_vat);
  const formattedTotal = formatNumberWithSpaces(total);
  const formattedVAT = formatNumberWithSpaces(vat);

  // Use services_per_days directly from backend
  const groupedServices = useMemo(() => {
    if (services_per_days && services_per_days.length > 0) {
      return groupServicesByDay(services_per_days);
    }
    
    if (services && services.length > 0) {
      let firstDate = 'Booking';
      if (booking_hours && booking_hours.length > 0 && booking_hours[0].date) {
        firstDate = booking_hours[0].date;
      } else if (booking.start_date) {
        firstDate = booking.start_date.split(' ')[0];
      }
      
      return [{
        date: firstDate,
        services: services.map(s => ({
          name: s.name || 'Service',
          quantity: s.quantity || 1,
          price: s.price || 0,
          type: s.type || null,
          location_type: s.location_type || 'day',
        }))
      }];
    }
    
    return [];
  }, [services_per_days, services, booking_hours, booking.start_date]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved':
        return styles.statusApproved;
      case 'waiting':
        return styles.statusPending;
      case 'rejected':
        return styles.statusRejected;
      default:
        return '';
    }
  };

  return (
    <div
      className={`${styles.bookingCard} ${styles[layout]}`}
      onClick={() => !showDetails && toggleDetails()}
    >
      <div className={styles.header}>
        {showDetails && (
          <CloseButton
            closeCallback={toggleDetails}
            className={styles.iconButton}
          />
        )}
      </div>
      <div className={styles.section1}>
        <div className={styles.images}>
          {place.images.slice(0, 1).map((image) => (
            <img
              key={image.id}
              src={image.thumb_image_url}
              alt={image.description}
              className={styles.image}
            />
          ))}
        </div>
        <div className={styles.details}>
          <div className={styles.detailsHeader}>
            <strong className={styles.title}>{place.name}</strong>
            <span className={styles.id}>
              {t("space.space")} {place.id}
            </span>
          </div>
          <div className={styles.detailsMain}>
            <span>
              {booking.id} - {booking.place.placeUsage.name} -
            </span>
            {Array.isArray(booking_hours) &&
              booking_hours.length > 0 &&
              booking_hours.map((slot, idx) => (
                <p key={`${booking_hours} ${slot.from} ${idx}`}>
                  {slot.date} {t("booking.step1.from")} {slot.from}{" "}
                  {t("booking.step1.to")} {slot.to} -
                </p>
              ))}
            {!booking?.trainee_booked && (
              <span className={styles.value}> {formatNumberWithSpaces(total_with_vat)} €</span>
            )}
          </div>
          <div className={`${styles.detailsAddress}`}>
            <div>
              <span
                className={styles.icon}
                style={{ backgroundImage: `url(${pinSvgIconPink})` }}
              ></span>
              <p>{`${place.address.address}, ${place.address.country}, ${place.address.city}`}</p>
            </div>
          </div>
        </div>
        
        <div className={styles.statusContainer}>
          <div className={`${styles.status} ${getStatusColor(booking.status)}`}>
            {getBookingStatus(booking, userType)}
            {actionLoading && <span className={styles.loadingSpinner} />}
          </div>
          {actionError && (
            <div className={styles.actionError}>
              {actionError}
            </div>
          )}
        </div>
      </div>
      
      {showDetails && (
        <>
          <div className={styles.bookingSummery}>
            <strong>{t("basket.booking-overview")}</strong>
          </div>
          <div className={styles.section2}>
            <div className={styles.section2Block}>
              <span className={styles.label}>
                {t("booking.step1.dates_and_hours")}:{" "}
              </span>
              <span>
                {Array.isArray(booking_hours) &&
                  booking_hours.length > 0 &&
                  booking_hours.map((slot, idx) => (
                    <p key={`${booking_hours} ${slot.from} ${idx}`}>
                      {slot.date} {t("booking.step1.from")} {slot.from}{" "}
                      {t("booking.step1.to")} {slot.to}
                    </p>
                  ))}
              </span>
            </div>
            {place?.placeUsage?.key !== "coworking" ? (
              <div className={styles.section2Block}>
                <span className={styles.label}>
                  {t("booking.step1.numberOfPeople")}:
                </span>{" "}
                <span> {capacity}</span>
              </div>
            ) : (
              <div className={styles.section2Block}>
                <span>{t("booking.step1.coworkingStations")}:</span>{" "}
                <span>{coworking_stations}</span>
              </div>
            )}
            {dispositionType && (
              <div className={styles.section2Block}>
                <span className={styles.label}>
                  {t("booking.step1.disposition_type")}{" "}
                </span>
                <div className={styles.iconContainer}>
                  <dispositionType.Icon className={styles.icon} />
                  <span>{dispositionType.name}</span>
                </div>
              </div>
            )}
            {promo_title && (
              <div className={styles.section2Block}>
                <span className={styles.label}>{t("basket.promoCode")}</span>
                <span>{promo_title}</span>
              </div>
            )}

            {!!(booking?.trainee_booked && booking?.user) && (
              <>
                <div className={styles.section2Block}>
                  <span className={styles.label}>
                    {t("basket.trainee_name")}
                  </span>
                  <span>{`${user.first_name} ${user.last_name}`}</span>
                </div>
                <div className={styles.section2Block}>
                  <span className={styles.label}>
                    {t("basket.training_id")}
                  </span>
                  <span>{`${booking.training.code}`}</span>
                </div>
              </>
            )}

            {userType === "provider" && (
              <div className={styles.section2Block}>
                <span className={styles.label}>
                  {t("dashboard.bookings.customer")}
                </span>
                <span>{`${user.first_name} ${user.last_name}`}</span>
              </div>
            )}
          </div>
          
          {/* ============================================================
              SERVICES SECTION - 3-Column Layout with Paid Onsite badge
              ============================================================ */}
          {groupedServices.length > 0 && (
            <div className={styles.section3}>
              <div className={styles.servicesHeader}>
                <span className={styles.servicesLabel}>
                  {t("dashboard.bookings.reserved-options")}
                </span>
                <span className={styles.servicesQuantityHeader}>
                  {t("dashboard.bookings.qty")}
                </span>
                <span className={styles.servicesPriceHeader}>
                  {t("dashboard.bookings.amount")}
                </span>
              </div>
              
              {groupedServices.map((dayGroup, groupIdx) => {
                return (
                  <div key={groupIdx} className={styles.dayServicesGroup}>
                    <div className={styles.dayHeader}>
                      <span className={styles.dayDate}>
                        📅 {dayGroup.date}
                      </span>
                      <span className={styles.serviceCount}>
                        ({dayGroup.services.length} {dayGroup.services.length === 1 ? 'service' : 'services'})
                      </span>
                    </div>
                    <div className={styles.dayServicesList}>
                      {dayGroup.services.map((service, idx) => {
                        const isFree = isServiceFree(service);
                        const isPaidOnsite = isServicePaidOnsite(service);
                        const totalPrice = getTotalPrice(service);
                        const serviceName = formatServiceName(service);
                        const quantityDisplay = formatQuantityWithUnit(service);
                        
                        return (
                          <div key={idx} className={styles.serviceItem}>
                            {/* Column 1: Service Name with badges */}
                            <div className={styles.serviceNameWrapper}>
                              <span className={styles.serviceName}>
                                {serviceName}
                              </span>
                              {isPaidOnsite && (
                                <span 
                                  className={styles.paidOnsiteBadge}
                                  title={t("booking.fullBookingPage.services.paidOnsiteTooltip", "This service is paid directly at the venue")}
                                >
                                  💳 Pay on-site
                                </span>
                              )}
                              {isFree && !isPaidOnsite && (
                                <span className={styles.freeBadge}>
                                  {t("booking.fullBookingPage.services.free", "Free")}
                                </span>
                              )}
                            </div>
                            
                            {/* Column 2: Quantity with "x" prefix (bold, left-aligned) */}
                            <div className={styles.serviceQuantity}>
                              {quantityDisplay}
                            </div>
                            
                            {/* Column 3: Price (bold, right-aligned) */}
                            {!booking?.trainee_booked && !isFree && (
                              <div className={styles.servicePrice}>{totalPrice} €</div>
                            )}
                            {!booking?.trainee_booked && isFree && (
                              <div className={styles.servicePrice}>-</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Fallback: flat services with 3-column layout */}
          {groupedServices.length === 0 && services && services.length > 0 && (
            <div className={styles.section3}>
              <div className={styles.servicesHeader}>
                <span className={styles.servicesLabel}>
                  {t("dashboard.bookings.reserved-options")}
                </span>
                <span className={styles.servicesQuantityHeader}>
                  {t("dashboard.bookings.qty")}
                </span>
                <span className={styles.servicesPriceHeader}>
                  {t("dashboard.bookings.amount")}
                </span>
              </div>
              
              {services.map((service, idx) => {
                const isFree = isServiceFree(service);
                const isPaidOnsite = isServicePaidOnsite(service);
                const totalPrice = (service.price || 0) * (service.quantity || 1);
                const serviceName = formatServiceName(service);
                const quantityDisplay = formatQuantityWithUnit(service);
                
                return (
                  <div key={idx} className={styles.serviceItem}>
                    <div className={styles.serviceNameWrapper}>
                      <span className={styles.serviceName}>
                        {serviceName}
                      </span>
                      {isPaidOnsite && (
                        <span 
                          className={styles.paidOnsiteBadge}
                          title={t("booking.fullBookingPage.services.paidOnsiteTooltip", "This service is paid directly at the venue")}
                        >
                          💳 Pay on-site
                        </span>
                      )}
                      {isFree && !isPaidOnsite && (
                        <span className={styles.freeBadge}>
                          {t("booking.fullBookingPage.services.free", "Free")}
                        </span>
                      )}
                    </div>
                    
                    <div className={styles.serviceQuantity}>
                      {quantityDisplay}
                    </div>
                    
                    {!booking?.trainee_booked && !isFree && (
                      <div className={styles.servicePrice}>{totalPrice} €</div>
                    )}
                    {!booking?.trainee_booked && isFree && (
                      <div className={styles.servicePrice}>-</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!booking?.trainee_booked && (
            <div className={styles.section4}>
              <div className={styles.priceDetails}>
                <div className={styles.item}>
                  <span className={styles.label}>
                    {t("basket.totalDutyFree")}:
                  </span>
                  <span className={styles.value}>{formattedTotal} €</span>
                </div>
                <div className={styles.item}>
                  <span className={styles.label}>{t("basket.VAT")}: </span>
                  <span className={styles.value}>{formattedVAT} €</span>
                </div>
                {total_with_vat && (
                  <div className={styles.item}>
                    <span className={styles.label}>
                      {t("basket.totalTaxesIncluded")}:
                    </span>
                    <span className={styles.value}>
                      {formattedTotalWithVAT} €
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className={styles.footer}>
        {!showDetails ? (
          <div>
            <FormButton
              size="medium"
              onClick={toggleDetails}
              variant="yellow_textOnly"
              label={`${
                showDetails ? "Hide Details \u25B2" : "More Details \u25BC"
              }`}
            />
          </div>
        ) : (
          <>
            <div className={styles.footerButtonsContainer}>
              <div className={styles.footerButton}>
                <FormButton
                  onClick={() => navigate("/static/cgu")}
                  variant="gray"
                  size="medium"
                  label={t("basket.viewCgv")}
                />
              </div>

              {userType === "provider" &&
                booking.status === "waiting" &&
                layout === LAYOUTS.CURRENT_BOOKINGS && (
                  <div className={styles.responseDropdownContainer}>
                    <div className={styles.customDropdown}>
                      <button 
                        className={styles.dropdownButton}
                        onClick={() => document.getElementById(`dropdown-${id}`).classList.toggle(styles.show)}
                        disabled={actionLoading}
                      >
                        <span>{t("dashboard.bookings.respondToDemand")}</span>
                        <span className={styles.arrow}>▼</span>
                      </button>
                      <div id={`dropdown-${id}`} className={styles.dropdownMenu}>
                        <div 
                          className={styles.dropdownItem}
                          onClick={() => handleResponseChange('approved')}
                        >
                          {t("buttons.confirm")}
                        </div>
                        <div 
                          className={styles.dropdownItem}
                          onClick={() => handleResponseChange('rejected')}
                        >
                          {t("dashboard.bookings.cancel")}
                        </div>
                      </div>
                    </div>
                    {actionError && (
                      <div className={styles.actionError}>
                        {actionError}
                      </div>
                    )}
                  </div>
                )}

              {userType === "seeker" &&
                layout === LAYOUTS.CURRENT_BOOKINGS &&
                booking.status !== "rejected" && (
                  <div className={styles.footerButton}>
                    <FormButton
                      onClick={() => onUserCancel(id)}
                      variant="secondary"
                      size="medium"
                      label={t("dashboard.bookings.cancelBooking")}
                      disabled={actionLoading}
                    />
                    {actionError && (
                      <div className={styles.actionError}>
                        {actionError}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingCard;